import { useState } from 'react';
import type { DeviceDetail, SlideshowSettings } from '@mantelpiece/shared';
import { api, type StoredSession } from '../lib/api';
import { go } from '../lib/route';
import type { Guard } from '../pages/Device';

interface Props {
  readonly session: StoredSession;
  readonly device: DeviceDetail;
  readonly guard: Guard;
  readonly onRenamed: () => void;
}

export const SettingsTab = ({ session, device, guard, onRenamed }: Props) => {
  const [settings, setSettings] = useState<SlideshowSettings>(device.settings);
  const [label, setLabel] = useState(device.label);
  const [saved, setSaved] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const update = <K extends keyof SlideshowSettings>(key: K, value: SlideshowSettings[K]) => {
    setSettings((current) => ({ ...current, [key]: value }));
    setSaved(false);
  };

  return (
    <section className="stack-loose">
      <div className="stack">
        <div className="section-head">
          <p className="eyebrow">This frame</p>
        </div>
        <div className="row">
          <label className="field">
            <span>Name</span>
            <input
              className="input"
              value={label}
              maxLength={40}
              onChange={(event) => setLabel(event.target.value)}
            />
          </label>
          <button
            type="button"
            className="button button-quiet"
            disabled={label.trim() === device.label || label.trim().length === 0}
            onClick={() =>
              void guard(async () => {
                await api.renameDevice(session.token, device.id, label.trim());
                onRenamed();
              })
            }
          >
            Rename
          </button>
        </div>
      </div>

      <div className="stack">
        <div className="section-head">
          <p className="eyebrow">How the frame behaves</p>
        </div>

        <div className="row">
          <label className="field">
            <span>Seconds per photo</span>
            <input
              className="input"
              type="number"
              min={5}
              max={120}
              value={settings.dwellSeconds}
              onChange={(event) => update('dwellSeconds', Number(event.target.value))}
            />
          </label>
          <label className="field">
            <span>Check for new photos every (s)</span>
            <input
              className="input"
              type="number"
              min={5}
              max={3600}
              value={settings.refreshSeconds}
              onChange={(event) => update('refreshSeconds', Number(event.target.value))}
            />
          </label>
        </div>

        <label className="check">
          <input
            type="checkbox"
            checked={settings.shuffle}
            onChange={(event) => update('shuffle', event.target.checked)}
          />
          Shuffle the order
        </label>
        <label className="check">
          <input
            type="checkbox"
            checked={settings.kenBurns}
            onChange={(event) => update('kenBurns', event.target.checked)}
          />
          Slow drift across each photo
        </label>
        <label className="check">
          <input
            type="checkbox"
            checked={settings.showCaption}
            onChange={(event) => update('showCaption', event.target.checked)}
          />
          Show who sent it, and when
        </label>

        <p className="note" style={{ marginTop: '0.5rem' }}>
          Drift keeps the panel from holding identical pixels in place. On an OLED that is not a
          nicety — see the burn-in note in the README before turning it off.
        </p>

        <div className="row" style={{ marginTop: '1rem' }}>
          <label className="field">
            <span>Dim from (hour)</span>
            <input
              className="input"
              type="number"
              min={0}
              max={23}
              value={settings.nightFromHour}
              onChange={(event) => update('nightFromHour', Number(event.target.value))}
            />
          </label>
          <label className="field">
            <span>Until (hour)</span>
            <input
              className="input"
              type="number"
              min={0}
              max={23}
              value={settings.nightToHour}
              onChange={(event) => update('nightToHour', Number(event.target.value))}
            />
          </label>
          <label className="field">
            <span>Night brightness</span>
            <input
              className="input"
              type="number"
              min={0.15}
              max={1}
              step={0.05}
              value={settings.nightDim}
              onChange={(event) => update('nightDim', Number(event.target.value))}
            />
          </label>
        </div>

        <div className="row">
          <button
            type="button"
            className="button"
            onClick={() =>
              void guard(async () => {
                setSettings(await api.saveSettings(session.token, device.id, settings));
                setSaved(true);
              })
            }
          >
            Save
          </button>
          {saved && <span className="note">Saved. The TV picks this up on its next check.</span>}
        </div>
      </div>

      <div className="stack">
        <div className="section-head">
          <p className="eyebrow">{device.permissions.canAdminister ? 'Danger' : 'Leaving'}</p>
        </div>
        {device.permissions.canAdminister ? (
          <>
            <p className="note">
              Deleting the frame disconnects its screens, removes everyone you shared it with, and
              takes its photos out of rotation.
            </p>
            <div className="row">
              <button
                type="button"
                className="button button-danger"
                onClick={() => {
                  if (!confirmDelete) {
                    setConfirmDelete(true);
                    return;
                  }
                  void guard(async () => {
                    await api.deleteDevice(session.token, device.id);
                    go('/');
                  });
                }}
              >
                {confirmDelete ? 'Really delete it' : 'Delete this frame'}
              </button>
              {confirmDelete && (
                <button
                  type="button"
                  className="button button-quiet"
                  onClick={() => setConfirmDelete(false)}
                >
                  Keep it
                </button>
              )}
            </div>
          </>
        ) : (
          <>
            <p className="note">
              You will stop seeing this frame. {device.ownerName} can share it with you again.
            </p>
            <div>
              <button
                type="button"
                className="button button-danger"
                onClick={() =>
                  void guard(async () => {
                    await api.leaveDevice(session.token, device.id);
                    go('/');
                  })
                }
              >
                Leave this frame
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
};
